/**
 * Internal types, not to be re-exported.
 */
export interface InboundMessage {
  /**
   * Namespace of the event.
   */
  namespace: string;

  /**
   * Whether the request was a successful one.
   */
  success: boolean;

  /**
   * An id of the call.
   */
  callId: number;

  /**
   * Data returned by the dashboard. Type depends on the call type.
   */
  data?: string;
}

export interface OutboundMessage {
  /**
   * An id of the call. Gets incremented with every sent message.
   * Used internally to match responses to their respective calls.
   */
  callId?: number;

  /**
   * The namespace that the event belongs to.
   */
  namespace: string;

  /**
   * Name of the event.
   */
  name: string;

  /**
   * Id of an app that the request concerns.
   */
  appId?: string;

  /**
   * Parameters of the call. Abitrary, depend on a call.
   */
  params?: any;

  /**
   * For the calls that expect multiple responses.
   */
  persistent?: boolean;

  /**
   * A callback that persistent messages will call every time their response comes.
   */
  callback?: (...args) => void;

  /**
   * For the calls that expect no responses.
   */
  transient?: boolean;
}

export interface PendingCallInfo {
  /**
   * A method to resolve call's promise.
   */
  resolve: (...args) => any;

  /**
   * A method to reject call's promise.
   */
  reject: (...args) => any;

  /**
   * A flag indicating whether the SDK should stop listening for more responses after the first arrives.
   */
  persistent: boolean;
}

export enum MessageNamespaces {
  App = 'app',
  Social = 'social',
  Dashboard = 'dashboard',
}

export enum MessageNames {
  GetAccount = 'app.getAccount',
  GetUser = 'app.getUser',
  GetDisplayGroups = 'app.getDisplays',
  GetSelectedDisplayId = 'app.getSelectedDisplayId',
  GetAssets = 'app.getAssets',
  GetAssetsPagination = 'app.getAssetsPagination',
  ReorderAssets = 'app.reorderAssets',
  GetLinkedGroups = 'app.getLinkedGroups',
  UpdateLinkedGroupMembers = 'app.updateLinkedGroupMembers',
  GetAssetsByIds = 'app.getAssetsByIds',
  GetAssetsForApp = 'app.getAssetsForApp',
  GetAssetRevision = 'app.getAssetRevision',
  BulkCreateAssets = 'app.bulkCreateAssets',
  BulkDeployAssets = 'app.bulkDeployAssets',
  SaveAsset = 'app.saveAsset',
  UpdateAssetOrder = 'app.updateAssetOrder',
  DeleteAsset = 'app.deleteAsset',
  TouchAsset = 'app.touchAsset',
  GetAssetNeedsApproval = 'app.getAssetNeedsApproval',
  GetDefaultAssets = 'app.getDefaultAssets',
  GetZoningApps = 'app.getZoningApps',
  AcquireZoningWidget = 'app.acquireZoningWidget',
  GetZoningData = 'app.getZoningData',
  EditZoningWidget = 'app.editZoningWidget',
  GetZoningLayoutAssets = 'app.getZoningLayoutAssets',
  GetFonts = 'app.getFonts',
  GetThemes = 'app.getThemes',
  GetTheme = 'app.getTheme',
  EditTheme = 'app.editTheme',
  SaveTheme = 'app.saveTheme',
  DeleteTheme = 'app.deleteTheme',
  HasFeatureFlag = 'app.hasFeatureFlag',
  GetDisplayGroupCustomProperties = 'app.getDisplayGroupCustomProperties',
  AppUrlChanged = 'app.appUrlChanged',
  LoadAccount = 'app.loadAccount',
  Click = 'dashboard.click',
  ConfirmUnsavedChanges = 'dashboard.unsavedChanges',
  ErrorIndicator = 'dashboard.indicator.error',
  LoadingIndicator = 'dashboard.indicator.loading',
  NavigateToZoning = 'dashboard.navigateToZoning',
  NavigateToUrl = 'dashboard.navigateToUrl',
  OpenConfirm = 'dashboard.confirm',
  PageError = 'dashboard.page.error',
  PageLoading = 'dashboard.page.loading',
  PageNotFound = 'dashboard.page.notFound',
  Preview = 'dashboard.preview',
  GetAssetPreviewData = 'dashboard.getAssetPreviewData',
  PreviewAsset = 'dashboard.previewAsset',
  SetDisplaySelectorCallback = 'dashboard.set.selectorCallback',
  SetDisplaySelectorVisibility = 'dashboard.set.selectorEnabled',
  SetHeaderButtons = 'dashboard.set.buttons',
  SetHeaderTitle = 'dashboard.set.title',
  ShowAdministrativeBreadcrumb = 'dashboard.showAdministrativeBreadcrumb',
  BreadCrumbType = 'dashboard.breadCrumbType',
  SuccessIndicator = 'dashboard.indicator.success',
  SwitchToView = 'dashboard.switchToView',
  Upload = 'dashboard.upload',
  ConnectData = 'dashboard.connectData',
  EditDataConnection = 'dashboard.editDataConnection',
  EncodeStatus = 'dashboard.encodeStatus',
  ResourcesEncodingStatus = 'dashboard.resourcesEncodingStatus',
  OpenContentIntervalSettingsDialog = 'dashboard.openContentIntervalSettingsDialog',
  OpenCopyTemplatesDialog = 'dashboard.openCopyTemplatesDialog',
  SetAppHasUnsavedChanges = 'dashboard.appHasUnsavedChanges',
  ClearQueryString = 'social.clearQueryString',
  AuthInstagram = 'social.authInstagram',
  AuthSlack = 'social.authSlack',
  AuthenticateSlack = 'social.authenticateSlack',
  AuthenticateTeamsUser = 'social.authenticateTeamsUser',
  AuthenticateTeamsAdmin = 'social.authenticateTeamsAdmin',
  GetInstagramAccounts = 'social.getInstagramAccounts',
  TwitterSubscriptionStatus = 'social.twitterSubscriptionStatus',
  LookupTwitterId = 'social.lookupTwitterId',
  GetSlackTeams = 'social.getSlackTeams',
  GetSlackChannels = 'social.getSlackChannels',
  GetTeamsChannels = 'social.getTeamsChannels',
  AddFacebookPage = 'social.addFacebookPage',
  GetFeeds = 'social.getFeeds',
  SaveFeed = 'social.saveFeed',
  DeleteFeed = 'social.deleteFeed',
  OpenPreapprovalDialog = 'social.openPreapprovalDialog',
  LoadAllItems = 'social.loadAllItems',
  ApproveItem = 'social.approveItem',
  RemoveItem = 'social.removeItem',
  FavoriteItem = 'social.favoriteItem',
  UnfavoriteItem = 'social.unfavoriteItem',
  BanItem = 'social.banItem',
  LoadBlacklist = 'social.loadBlacklist',
  UnbanUser = 'social.unbanUser',
  RefreshFeed = 'social.refreshFeed',
  CreateAssetVersion = 'app.createAssetVersion',
  GetAssetsHistoryById = 'app.getAssetsHistoryById',
  GetAssetsHistoryByAssetId = 'app.getAssetsHistoryByAssetId',
  DeleteAssetHistory = 'app.deleteAssetHistory',
  RestoreVersion = 'app.restoreVersion',
  RefreshTwitterFeed = 'social.refreshTwitterFeed',
  ProcessAssetResource = 'dashboard.processAssetResource',
  DuplicateAsset = 'app.duplicateAsset'
}
